// 261010 Epic 2 / P5d — định vị câu AI trích dẫn trong khung prompt (thuần: không antd/React).
// Cùng luật so khớp với BE (Phase doc 5d §3): NFC · gộp mọi khoảng trắng thành một dấu cách · không phân biệt hoa thường. Không dựng RegExp từ câu trích (ký tự đặc biệt = chữ thường).
// Chỉ khớp NGUYÊN VĂN (sau chuẩn hoá): không tìm gần đúng — câu không có trong prompt đang lưu thì báo "không thấy", không đoán chỗ khác.
import type { PromptPick } from '../../types/filmStepReview';

export interface PromptSegment { text: string; mark: boolean }

interface Normalized { s: string; from: number[]; to: number[] }

/** Chuẩn hoá `text` và nhớ, với MỖI đơn vị chữ của kết quả, đoạn [from, to) của chữ GỐC sinh ra nó (để cắt lại đúng chữ gốc kể cả khi xuống dòng / đổi độ dài lúc hạ chữ). */
function normalize(text: string): Normalized {
  const out: string[] = []; const from: number[] = []; const to: number[] = [];
  let i = 0;
  while (i < text.length) {
    const cp = text.codePointAt(i) as number;
    const ch = String.fromCodePoint(cp);
    if (/\s/.test(ch)) {
      let j = i + ch.length;
      while (j < text.length && /\s/.test(String.fromCodePoint(text.codePointAt(j) as number))) j += String.fromCodePoint(text.codePointAt(j) as number).length;
      out.push(' '); from.push(i); to.push(j);
      i = j;
    } else {
      const end = i + ch.length;
      for (const u of ch.toLowerCase()) for (let k = 0; k < u.length; k++) { out.push(u[k]); from.push(i); to.push(end); }
      i = end;
    }
  }
  return { s: out.join(''), from, to };
}

const has = (s: string | null | undefined): s is string => typeof s === 'string' && s.trim().length > 0;

export type EvidenceSourceKind = 'REVIEWED_FINAL' | 'REVIEWED_STORED' | 'SAVED_NOW' | 'ENGINE_NOW';
export interface EvidenceHit { kind: EvidenceSourceKind; label: string; segments: PromptSegment[] }
const SOURCE_LABEL: Record<EvidenceSourceKind, string> = {
  REVIEWED_FINAL: 'Đang hiện: bản gửi engine mà giám khảo đã thấy (lúc chấm).',
  REVIEWED_STORED: 'Đang hiện: prompt đã lưu lúc chấm (chưa đổi token thành @ImageN).',
  SAVED_NOW: 'Đang hiện: prompt đang lưu hiện tại — có thể đã khác bản giám khảo thấy.',
  ENGINE_NOW: 'Đang hiện: bản gửi engine hiện tại (phiếu này không kèm bản lúc chấm) — có thể đã khác bản giám khảo thấy.',
};

/** Phiếu KHÔNG kèm `finalPrompt` lúc chấm (phiếu cũ / snapshot thiếu) ⇒ mới cần hỏi bản gửi engine hiện tại. Có snapshot ⇒ không bao giờ gọi (bản hiện tại có thể khác bản giám khảo đã thấy). */
export const needsEngineFetch = (pick: PromptPick): boolean => !has(pick.finalPrompt);

/**
 * Tìm câu AI trích theo thứ tự: (1) `finalPrompt` lúc chấm — đúng bản giám khảo thấy · (2) `storedPrompt` lúc chấm · (3) prompt đang lưu hiện tại · (4) bản gửi engine hiện tại (CHỈ khi phiếu không có `finalPrompt`).
 * Không thấy ở các bản prompt ⇒ `hit = null`; `inReviewInput` cho biết câu có nằm ở phần đề (kịch bản / shot / ảnh đính) hay không — để hiện chữ chứ không báo lỗi.
 */
export function findEvidence(pick: PromptPick, savedPrompt: string | null | undefined, engineNow?: string | null): { hit: EvidenceHit | null; inReviewInput: boolean } {
  const candidates: Array<{ kind: EvidenceSourceKind; text: string | null | undefined }> = [
    { kind: 'REVIEWED_FINAL', text: pick.finalPrompt },
    { kind: 'REVIEWED_STORED', text: pick.storedPrompt },
    { kind: 'SAVED_NOW', text: savedPrompt },
    ...(needsEngineFetch(pick) ? [{ kind: 'ENGINE_NOW' as const, text: engineNow }] : []),
  ];
  for (const c of candidates) {
    if (!has(c.text)) continue;
    const segments = highlightSegments(c.text, pick.quote);
    if (segments) return { hit: { kind: c.kind, label: SOURCE_LABEL[c.kind], segments }, inReviewInput: false };
  }
  return { hit: null, inReviewInput: has(pick.reviewInput) && highlightSegments(pick.reviewInput, pick.quote) !== null };
}

/**
 * Cắt `text` (NFC) thành các đoạn, đoạn chứa lần xuất hiện ĐẦU TIÊN của `quote` có `mark = true`. Không thấy / trích rỗng / prompt rỗng ⇒ `null`.
 * Ghép `text` của mọi đoạn luôn bằng đúng `text` đã NFC (không mất / không thêm chữ).
 */
export function highlightSegments(text: string, quote: string): PromptSegment[] | null {
  if (typeof text !== 'string' || typeof quote !== 'string' || !text) return null;
  const full = text.normalize('NFC');
  const q = normalize(quote.normalize('NFC').trim()).s;
  if (!q) return null;
  const n = normalize(full);
  const at = n.s.indexOf(q);
  if (at < 0) return null;
  const start = n.from[at];
  const end = n.to[at + q.length - 1];
  const segs: PromptSegment[] = [
    { text: full.slice(0, start), mark: false },
    { text: full.slice(start, end), mark: true },
    { text: full.slice(end), mark: false },
  ];
  return segs.filter((x) => x.text.length > 0);
}
